import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  TrendingUpIcon,
  TrendingDownIcon,
  CurrencyRupeeIcon,
  XCircleIcon,
  CheckCircleIcon,
  ClockIcon,
  UsersIcon,
  ChartBarIcon,
} from "@heroicons/react/outline";
import {
  getCancellationStatsThunk,
  getRefundStatsThunk,
  getCollectionStatsThunk,
} from "../../../store/thunks/jewelleryPlan/cancellationThunks";
import {
  selectCancellationStats,
  selectRefundStats,
  selectCollectionStats,
  selectStatsLoading,
  selectStatsError,
} from "../../../store/slices/jewelleryPlan/cancellationSlice";

const CancellationStatistics = () => {
  const dispatch = useDispatch();

  const cancellationStats = useSelector(selectCancellationStats);
  const refundStats = useSelector(selectRefundStats);
  const collectionStats = useSelector(selectCollectionStats);
  const loading = useSelector(selectStatsLoading);
  const error = useSelector(selectStatsError);

  const [dateRange, setDateRange] = useState({
    startDate: new Date(new Date().setMonth(new Date().getMonth() - 3))
      .toISOString()
      .split("T")[0],
    endDate: new Date().toISOString().split("T")[0],
  });

  const [branchId, setBranchId] = useState("");

  useEffect(() => {
    loadStats();
  }, [dispatch, dateRange, branchId]);

  const loadStats = () => {
    const filters = {
      startDate: dateRange.startDate,
      endDate: dateRange.endDate,
    };
    if (branchId) filters.branchId = branchId;

    dispatch(getCancellationStatsThunk(filters));
    dispatch(getRefundStatsThunk(filters));
    dispatch(getCollectionStatsThunk(filters));
  };

  const formatAmount = (amount) => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      maximumFractionDigits: 0,
    }).format(amount || 0);
  };

  const formatNumber = (num) => {
    return new Intl.NumberFormat("en-IN").format(num || 0);
  };

  const getCategoryLabel = (category) => {
    const labels = {
      CUSTOMER_REQUEST: "Customer Request",
      FINANCIAL_HARDSHIP: "Financial Hardship",
      MEDICAL_EMERGENCY: "Medical Emergency",
      DEATH: "Death",
      RELOCATION: "Relocation",
      FRAUD_SUSPECTED: "Fraud Suspected",
      ADMIN_ACTION: "Admin Action",
      POLICY_VIOLATION: "Policy Violation",
      OTHER: "Other",
    };
    return labels[category] || category;
  };

  const getStatusLabel = (status) => {
    const labels = {
      PENDING: "Pending",
      APPROVED: "Approved",
      PROCESSING: "Processing",
      COMPLETED: "Completed",
      FAILED: "Failed",
      CANCELLED: "Cancelled",
      NOTIFIED: "Notified",
      PARTIAL_COLLECTED: "Partial Collected",
      COLLECTED: "Collected",
      WAIVED: "Waived",
      WRITTEN_OFF: "Written Off",
    };
    return labels[status] || status;
  };

  if (loading && !cancellationStats && !refundStats && !collectionStats) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-text">
          Cancellation Analytics & Statistics
        </h1>
        <p className="mt-1 text-sm text-neutral-dark">
          Comprehensive view of cancellations, refunds, and collections
        </p>
      </div>

      {/* Filters */}
      <div className="bg-background rounded-lg shadow p-4 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-text mb-1">
              Start Date
            </label>
            <input
              type="date"
              value={dateRange.startDate}
              onChange={(e) =>
                setDateRange({ ...dateRange, startDate: e.target.value })
              }
              className="w-full px-3 py-2 border border-neutral-light rounded-lg focus:ring-primary focus:border-primary"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-text mb-1">
              End Date
            </label>
            <input
              type="date"
              value={dateRange.endDate}
              onChange={(e) =>
                setDateRange({ ...dateRange, endDate: e.target.value })
              }
              className="w-full px-3 py-2 border border-neutral-light rounded-lg focus:ring-primary focus:border-primary"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-text mb-1">
              Branch (Optional)
            </label>
            <input
              type="text"
              value={branchId}
              onChange={(e) => setBranchId(e.target.value)}
              placeholder="Enter branch ID..."
              className="w-full px-3 py-2 border border-neutral-light rounded-lg focus:ring-primary focus:border-primary"
            />
          </div>
        </div>
      </div>

      {/* Error Display */}
      {error && (
        <div className="mb-6 bg-danger-light border border-danger rounded-lg p-4">
          <div className="flex items-start">
            <XCircleIcon className="h-5 w-5 text-danger mt-0.5 mr-2" />
            <div>
              <p className="text-sm font-medium text-danger-dark">Error</p>
              <p className="text-sm text-danger-dark mt-1">{error}</p>
            </div>
          </div>
        </div>
      )}

      {/* Overview Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-6">
        <div className="bg-background rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-neutral-dark">Total Cancellations</p>
              <p className="text-3xl font-bold text-text mt-2">
                {formatNumber(
                  cancellationStats?.reduce(
                    (sum, stat) => sum + stat.count,
                    0
                  ) || 0
                )}
              </p>
            </div>
            <div className="h-12 w-12 bg-danger-light rounded-full flex items-center justify-center">
              <XCircleIcon className="h-6 w-6 text-danger" />
            </div>
          </div>
        </div>

        <div className="bg-background rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-neutral-dark">Total Refunds</p>
              <p className="text-3xl font-bold text-text mt-2">
                {formatAmount(
                  refundStats?.reduce(
                    (sum, stat) => sum + stat.totalAmount,
                    0
                  ) || 0
                )}
              </p>
            </div>
            <div className="h-12 w-12 bg-success-light rounded-full flex items-center justify-center">
              <CurrencyRupeeIcon className="h-6 w-6 text-success" />
            </div>
          </div>
        </div>

        <div className="bg-background rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-neutral-dark">Total Collections</p>
              <p className="text-3xl font-bold text-text mt-2">
                {formatAmount(
                  collectionStats?.reduce(
                    (sum, stat) => sum + stat.totalAmount,
                    0
                  ) || 0
                )}
              </p>
            </div>
            <div className="h-12 w-12 bg-warning-light rounded-full flex items-center justify-center">
              <TrendingUpIcon className="h-6 w-6 text-warning" />
            </div>
          </div>
        </div>

        <div className="bg-background rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-neutral-dark">Outstanding</p>
              <p className="text-3xl font-bold text-text mt-2">
                {formatAmount(
                  collectionStats?.reduce(
                    (sum, stat) => sum + stat.remainingAmount,
                    0
                  ) || 0
                )}
              </p>
            </div>
            <div className="h-12 w-12 bg-secondary-light rounded-full flex items-center justify-center">
              <ClockIcon className="h-6 w-6 text-secondary" />
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        {/* Cancellation by Category */}
        <div className="bg-background rounded-lg shadow p-6">
          <h2 className="text-lg font-semibold text-text mb-4 flex items-center">
            <ChartBarIcon className="h-5 w-5 mr-2" />
            Cancellations by Category
          </h2>
          {cancellationStats && cancellationStats.length > 0 ? (
            <div className="space-y-3">
              {cancellationStats.map((stat, index) => {
                const total = cancellationStats.reduce(
                  (sum, s) => sum + s.count,
                  0
                );
                const percentage = ((stat.count / total) * 100).toFixed(1);

                return (
                  <div key={index}>
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-sm text-text">
                        {getCategoryLabel(stat._id)}
                      </span>
                      <span className="text-sm font-medium text-text">
                        {stat.count} ({percentage}%)
                      </span>
                    </div>
                    <div className="w-full bg-neutral-light rounded-full h-2">
                      <div
                        className="bg-primary h-2 rounded-full transition-all"
                        style={{ width: `${percentage}%` }}
                      ></div>
                    </div>
                    <div className="mt-1 flex justify-between text-xs text-neutral-dark">
                      <span>Refunds: {formatAmount(stat.totalRefunded)}</span>
                      <span>
                        Collections: {formatAmount(stat.totalCollected)}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-8">
              <ChartBarIcon className="h-12 w-12 text-neutral mx-auto mb-2" />
              <p className="text-neutral-dark">
                No cancellation data available
              </p>
            </div>
          )}
        </div>

        {/* Refund Status Distribution */}
        <div className="bg-background rounded-lg shadow p-6">
          <h2 className="text-lg font-semibold text-text mb-4 flex items-center">
            <CurrencyRupeeIcon className="h-5 w-5 mr-2" />
            Refund Status Distribution
          </h2>
          {refundStats && refundStats.length > 0 ? (
            <div className="space-y-4">
              {refundStats.map((stat, index) => {
                const statusColors = {
                  PENDING: "warning",
                  APPROVED: "primary",
                  PROCESSING: "secondary",
                  COMPLETED: "success",
                  FAILED: "danger",
                  CANCELLED: "neutral",
                };
                const color = statusColors[stat._id] || "neutral";

                return (
                  <div
                    key={index}
                    className={`p-4 bg-${color}-light rounded-lg border border-${color}`}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <span
                        className={`text-sm font-medium text-${color}-dark`}
                      >
                        {getStatusLabel(stat._id)}
                      </span>
                      <span className={`text-lg font-bold text-${color}-dark`}>
                        {stat.count}
                      </span>
                    </div>
                    <div className="space-y-1 text-xs">
                      <div className="flex justify-between">
                        <span className="text-neutral-dark">Total Amount</span>
                        <span className="font-medium">
                          {formatAmount(stat.totalAmount)}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-neutral-dark">Average</span>
                        <span className="font-medium">
                          {formatAmount(stat.avgAmount)}
                        </span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-8">
              <CurrencyRupeeIcon className="h-12 w-12 text-neutral mx-auto mb-2" />
              <p className="text-neutral-dark">No refund data available</p>
            </div>
          )}
        </div>
      </div>

      {/* Collection Status */}
      <div className="bg-background rounded-lg shadow p-6 mb-6">
        <h2 className="text-lg font-semibold text-text mb-4 flex items-center">
          <TrendingUpIcon className="h-5 w-5 mr-2" />
          Collection Status Overview
        </h2>
        {collectionStats && collectionStats.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {collectionStats.map((stat, index) => {
              const collectionRate =
                stat.totalAmount > 0
                  ? ((stat.collectedAmount / stat.totalAmount) * 100).toFixed(1)
                  : 0;

              const statusColors = {
                PENDING: "warning",
                NOTIFIED: "secondary",
                PARTIAL_COLLECTED: "primary",
                COLLECTED: "success",
                WAIVED: "neutral",
                WRITTEN_OFF: "danger",
              };
              const color = statusColors[stat._id] || "neutral";

              return (
                <div
                  key={index}
                  className={`p-4 bg-${color}-light rounded-lg border border-${color}`}
                >
                  <div className="flex items-center justify-between mb-3">
                    <span className={`text-sm font-medium text-${color}-dark`}>
                      {getStatusLabel(stat._id)}
                    </span>
                    <span className={`text-2xl font-bold text-${color}-dark`}>
                      {stat.count}
                    </span>
                  </div>
                  <div className="space-y-2">
                    <div>
                      <div className="flex justify-between text-xs mb-1">
                        <span className="text-neutral-dark">Total Due</span>
                        <span className="font-medium">
                          {formatAmount(stat.totalAmount)}
                        </span>
                      </div>
                      <div className="flex justify-between text-xs mb-1">
                        <span className="text-neutral-dark">Collected</span>
                        <span className="font-medium text-success-dark">
                          {formatAmount(stat.collectedAmount)}
                        </span>
                      </div>
                      <div className="flex justify-between text-xs mb-2">
                        <span className="text-neutral-dark">Remaining</span>
                        <span className="font-medium text-warning-dark">
                          {formatAmount(stat.remainingAmount)}
                        </span>
                      </div>
                    </div>
                    <div>
                      <div className="flex justify-between text-xs mb-1">
                        <span className="text-neutral-dark">
                          Collection Rate
                        </span>
                        <span className="font-medium">{collectionRate}%</span>
                      </div>
                      <div className="w-full bg-neutral-light rounded-full h-2">
                        <div
                          className="bg-success h-2 rounded-full transition-all"
                          style={{ width: `${collectionRate}%` }}
                        ></div>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="text-center py-8">
            <TrendingUpIcon className="h-12 w-12 text-neutral mx-auto mb-2" />
            <p className="text-neutral-dark">No collection data available</p>
          </div>
        )}
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {/* Refund Summary */}
        <div className="bg-success-light border border-success rounded-lg p-6">
          <h3 className="text-lg font-semibold text-success-dark mb-4">
            Refund Summary
          </h3>
          <div className="space-y-3">
            <div className="flex justify-between">
              <span className="text-sm text-success-dark">Total Requests</span>
              <span className="font-bold">
                {formatNumber(
                  refundStats?.reduce((sum, stat) => sum + stat.count, 0) || 0
                )}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm text-success-dark">Total Amount</span>
              <span className="font-bold">
                {formatAmount(
                  refundStats?.reduce(
                    (sum, stat) => sum + stat.totalAmount,
                    0
                  ) || 0
                )}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm text-success-dark">Avg per Request</span>
              <span className="font-bold">
                {formatAmount(
                  refundStats?.reduce((sum, stat) => sum + stat.avgAmount, 0) /
                    (refundStats?.length || 1) || 0
                )}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm text-success-dark">Completed</span>
              <span className="font-bold">
                {formatNumber(
                  refundStats?.find((s) => s._id === "COMPLETED")?.count || 0
                )}
              </span>
            </div>
          </div>
        </div>

        {/* Collection Summary */}
        <div className="bg-warning-light border border-warning rounded-lg p-6">
          <h3 className="text-lg font-semibold text-warning-dark mb-4">
            Collection Summary
          </h3>
          <div className="space-y-3">
            <div className="flex justify-between">
              <span className="text-sm text-warning-dark">Total Requests</span>
              <span className="font-bold">
                {formatNumber(
                  collectionStats?.reduce((sum, stat) => sum + stat.count, 0) ||
                    0
                )}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm text-warning-dark">Total Due</span>
              <span className="font-bold">
                {formatAmount(
                  collectionStats?.reduce(
                    (sum, stat) => sum + stat.totalAmount,
                    0
                  ) || 0
                )}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm text-warning-dark">Collected</span>
              <span className="font-bold text-success-dark">
                {formatAmount(
                  collectionStats?.reduce(
                    (sum, stat) => sum + stat.collectedAmount,
                    0
                  ) || 0
                )}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm text-warning-dark">Outstanding</span>
              <span className="font-bold text-danger-dark">
                {formatAmount(
                  collectionStats?.reduce(
                    (sum, stat) => sum + stat.remainingAmount,
                    0
                  ) || 0
                )}
              </span>
            </div>
          </div>
        </div>

        {/* Key Metrics */}
        <div className="bg-primary-light border border-primary rounded-lg p-6">
          <h3 className="text-lg font-semibold text-primary-dark mb-4">
            Key Metrics
          </h3>
          <div className="space-y-3">
            <div className="flex justify-between">
              <span className="text-sm text-primary-dark">
                Cancellation Rate
              </span>
              <span className="font-bold">
                {cancellationStats && cancellationStats.length > 0
                  ? "N/A"
                  : "0%"}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm text-primary-dark">Avg Refund Time</span>
              <span className="font-bold">N/A</span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm text-primary-dark">Collection Rate</span>
              <span className="font-bold">
                {collectionStats &&
                collectionStats.reduce(
                  (sum, stat) => sum + stat.totalAmount,
                  0
                ) > 0
                  ? (
                      (collectionStats.reduce(
                        (sum, stat) => sum + stat.collectedAmount,
                        0
                      ) /
                        collectionStats.reduce(
                          (sum, stat) => sum + stat.totalAmount,
                          0
                        )) *
                      100
                    ).toFixed(1) + "%"
                  : "0%"}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm text-primary-dark">Write-off Rate</span>
              <span className="font-bold">
                {collectionStats
                  ? (
                      (collectionStats.filter((s) => s._id === "WAIVED")
                        .length /
                        collectionStats.reduce(
                          (sum, stat) => sum + stat.count,
                          0
                        )) *
                      100
                    ).toFixed(1) + "%"
                  : "0%"}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Export Section */}
      <div className="mt-6 bg-background rounded-lg shadow p-6">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold text-text">Export Reports</h3>
            <p className="text-sm text-neutral-dark mt-1">
              Download detailed reports for the selected date range
            </p>
          </div>
          <div className="flex space-x-3">
            <button className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark">
              Export to Excel
            </button>
            <button className="px-4 py-2 border border-primary text-primary rounded-lg hover:bg-primary-light">
              Export to PDF
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CancellationStatistics;
